import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import {
  Client,
  ClientActionTypes,
  ClientOnServerCreated,
  ClientOnServerUpdated,
  selectClientById,
  selectClientError,
  selectClientLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { GeneralValidationMessagesService } from '../../../../../../core/services/general-validation-messages.service';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-client-edit',
  templateUrl: './client-edit.component.html',
  styleUrls: ['client-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientEditComponent implements OnInit, OnDestroy {
  client: Client;
  oldClient: Client;
  clientForm: FormGroup;
  hasFormErrors = false;
  isViewMode: boolean;
  clicked: boolean;
  private headerMargin: number;
  private subscriptions: Subscription[] = [];
  urbanizationValue;
  Address1Value;
  Address2Value;
  cityValue;
  zipValue;
  params: any;
  @Input() entity_id;
  @Input() fromCanvas;
  // tslint:disable-next-line:no-output-on-prefix
  @Output() focusedViewOpen = new EventEmitter<any>();
  @Output() navigateToContact = new EventEmitter<any>();

  view = false;
  tabs = [
    { index: 0, label: 'General Info', icon: 'mat-tab-label-icon fa fa-info', active: true },
    {
      index: 1,
      label: 'Relations',
      icon: 'mat-tab-label-icon fas fa-people-arrows',
      active: false,
      permission: 'canViewRelation',
    },
    {
      index: 2,
      label: 'Documents',
      icon: 'mat-tab-label-icon icon-lg fa fa-file-alt',
      active: false,
    },
  ];
  loadingData = false;
  constructor(
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private clientFB: FormBuilder,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private globalService: GeneralValidationMessagesService,
  ) {}
  closeDialog(e) {
    if (e) {
      this.focusedViewOpen.emit(true);
      this.navigateToContact.emit(true);
    }
  }
  ngOnInit() {
    if (this.entity_id) {
      this.globalService.getEntitiesById('clients', this.entity_id).subscribe((res) => {
        if (res) {
          this.client = res.data;
          this.loadClient();
          this.cdr.detectChanges();
        }
      });
      return;
    }
    // this line due to make the navigation service return to clients list if there is no ->
    // navigation stack saved before in special cases
    this.navigationService.setDefaultRoot('/lists/clients');

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
      params.tab_index ? this.changeTab(+params.tab_index) : this.changeTab(0);
    });
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/clients');
      }
    });
    const errorSubscription = this.store
      .pipe(select(selectClientError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Update,
            3000,
            true,
            false,
          );
          this.clicked = false;
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectClientLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case ClientActionTypes.ClientCreated:
              successMsg = this.translate.instant('CLIENT.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case ClientActionTypes.ClientUpdated:
              successMsg = this.translate.instant('CLIENT.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
            this.goBack();
          }
        }
      });
    this.subscriptions.push(successSubscription);
    this.subscriptions.push(userSub);
    const routeSub = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        this.store.pipe(select(selectClientById(id))).subscribe((result) => {
          if (result) {
            this.client = result;
            this.loadClient();
            return;
          }
          const clientById = this.globalService.getEntitiesById('clients', id).subscribe((res) => {
            if (res) {
              this.client = res.data;
              this.loadClient();
              this.cdr.detectChanges();
              return;
            }
          });
          this.subscriptions.push(clientById);
        });
      } else {
        this.client = new Client();
        this.loadClient();
      }
      this.cdr.detectChanges();
    });
    this.subscriptions.push(routeSub);
    // sticky portlet header
    window.onload = () => {
      const style = getComputedStyle(document.getElementById('kt_header'));
      this.headerMargin = parseInt(style.height, 0);
    };
  }

  firstLine() {
    const titleOne = document.querySelector('.title-one') as HTMLElement;
    const text =
      (this.client.first_name ? this.client.first_name : '') +
      ' ' +
      (this.client.middle_name ? this.client.middle_name : '');
    const textLength = text.length;
    const fontSize = Math.floor(220 / textLength);
    const newSize = (fontSize + 5) * 10 - 15;
    titleOne.style.fontSize = textLength < 16 ? 20 + 'px' : newSize + '%';
    return text;
  }

  secondLine() {
    const titleTwo = document.querySelector('.title-two') as HTMLElement;
    const text2 =
      (this.client.last_name ? this.client.last_name : '') +
      ' ' +
      (this.client.last_name2 ? this.client.last_name2 : '');
    const textLength2 = text2.length;
    const fontSize2 = Math.floor(220 / textLength2);
    const newSize2 = (fontSize2 + 5) * 10 - 15;
    titleTwo.style.fontSize = textLength2 < 16 ? 20 + 'px' : newSize2 + '%';
    return text2;
  }

  loadClient() {
    if (!this.client) {
      this.goBack();
      return;
    }
    this.oldClient = Object.assign({}, this.client);
    this.initClient();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  initClient() {
    this.createForm();
    if (!this.client.id) {
      this.isViewMode = false;
    } else
      this.isViewMode =
        this.activatedRoute.url[`_value`][1] &&
        this.activatedRoute.url[`_value`][1].path === 'view';
  }

  createForm() {
    this.clientForm = this.clientFB.group({
      first_name: [this.client.first_name, Validators.required],
      last_name: [this.client.last_name || '', Validators.required],
      middle_name: [this.client.middle_name],
      last_name2: [this.client.last_name2],
      domain: [this.client.domain],
      email: [
        this.client.email,
        Validators.pattern(
          /^\s*([A-Za-z0-9\-_.]+@[A-Za-z0-9]+\.[a-z]+;\s*)*([A-Za-z0-9\-_.]+@[A-Za-z0-9]+\.[a-z]+\s*)?$/,
        ),
      ],
      cell_phone: [
        this.client.cell_phone && this.client.cell_phone !== ''
          ? '(' +
            this.client.cell_phone.substring(0, 3) +
            ') ' +
            this.client.cell_phone.substring(3, 6) +
            '-' +
            this.client.cell_phone.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      office_phone: [
        this.client.office_phone && this.client.office_phone !== ''
          ? '(' +
            this.client.office_phone.substring(0, 3) +
            ') ' +
            this.client.office_phone.substring(3, 6) +
            '-' +
            this.client.office_phone.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      fax: [
        this.client.fax && this.client.fax !== ''
          ? '(' +
            this.client.fax.substring(0, 3) +
            ') ' +
            this.client.fax.substring(3, 6) +
            '-' +
            this.client.fax.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      communication_method: [this.client.communication_method],
      type: [this.client.type],
      urbanization: [this.client.urbanization, Validators.maxLength(48)],
      city: [this.client.city, Validators.compose([Validators.required, Validators.maxLength(30)])],
      state: [this.client.state, Validators.required],
      zip: [
        this.client.zip,
        Validators.compose([Validators.required, Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]),
      ],
      address1: [
        this.client.address_1,
        Validators.compose([Validators.required, Validators.maxLength(48)]),
      ],
      address2: [this.client.address_2, Validators.maxLength(48)],
      order_status_update: [
        this.client.order_status_update ? this.client.order_status_update : false,
      ],
      remind_zero_refill: [this.client.remind_zero_refill ? this.client.remind_zero_refill : false],
      note: [this.client.note],
      is_active: [this.client.is_active ? this.client.is_active : false],
      is_company: [this.getClientClass(this.client.client_class)],
    });
    if (
      this.activatedRoute.url[`_value`][1] &&
      this.activatedRoute.url[`_value`][1].path === 'view'
    ) {
      this.clientForm.disable();
    }
    this.urbanizationValue = this.client.urbanization;
    this.Address1Value = this.client.address_1;
    this.Address2Value = this.client.address_2;
    this.cityValue = this.client.city;
    this.zipValue = this.client.zip;
    this.loadingData = true;
  }

  goBack() {
    if (this.entity_id) {
      return;
    }
    this.navigationService.goBack();
  }

  getClientClass(val) {
    switch (val) {
      case 'INDIVIDUAL':
        return false;
      case 'COMPANY':
        return true;
      default:
        return true;
    }
  }

  reset() {
    this.client = Object.assign({}, this.oldClient);
    this.createForm();
    this.clientForm.controls[`entity_type`].enable();
    this.hasFormErrors = false;
    this.clientForm.markAsPristine();
    this.clientForm.markAsUntouched();
    this.clientForm.updateValueAndValidity();
  }

  onSubmit() {
    this.clientForm.get('is_company').value
      ? this.clientForm.get('last_name').clearValidators()
      : this.clientForm.get('last_name').setValidators(Validators.required);
    this.clientForm.controls['last_name'].updateValueAndValidity();
    this.hasFormErrors = false;
    this.clicked = true;
    const controls = this.clientForm.controls;
    if (this.clientForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      return;
    }
    const editedClient = this.prepareClient();
    if (editedClient.id > 0) {
      this.updateClient(editedClient);
      return;
    }
    this.addClient(editedClient);
  }

  prepareClient(): Client {
    const controls = this.clientForm.controls;
    const _client = new Client();
    _client.id = this.client.id;
    _client.first_name = controls.first_name.value;
    _client.middle_name = controls.middle_name.value;
    _client.last_name = controls.last_name.value;
    _client.last_name2 = controls.last_name2.value;
    _client.domain = controls.domain.value;
    _client.email = controls.email.value;
    _client.cell_phone =
      controls.cell_phone.value !== null
        ? controls.cell_phone.value.replace(/\D/g, '')
        : controls.cell_phone.value;
    _client.office_phone =
      controls.office_phone.value !== null
        ? controls.office_phone.value.replace(/\D/g, '')
        : controls.office_phone.value;
    _client.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _client.communication_method = controls.communication_method.value;
    _client.type = controls.type.value;
    _client.city = controls.city.value;
    _client.state = controls.state.value.toUpperCase();
    _client.zip = controls.zip.value;
    _client.address_1 = controls.address1.value;
    _client.address_2 = controls.address2.value;
    _client.remind_zero_refill = controls.remind_zero_refill.value;
    _client.order_status_update = controls.order_status_update.value;
    _client.note = controls.note.value;
    _client.is_active = controls.is_active.value;
    _client.client_class = controls.is_company.value ? 'COMPANY' : 'INDIVIDUAL';
    _client.urbanization = controls.urbanization.value;
    _client.client_id = this.client?.client_id || '';

    for (const [key, value] of Object.entries(_client)) {
      if (typeof value === 'string') {
        _client[key] = value.trim();
      }
    }
    return _client;
  }

  addClient(_client: Client) {
    this.store.dispatch(new ClientOnServerCreated({ client: _client }));
  }

  updateClient(_client: Client) {
    const updateClient: Update<Client> = {
      id: _client.id,
      changes: _client,
    };

    this.store.dispatch(
      new ClientOnServerUpdated({
        partialClient: updateClient,
        client: _client,
      }),
    );
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.clientForm?.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  getStatusString(): string {
    switch (this.clientForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  getOrderStatusString(): string {
    switch (this.clientForm.get('order_status_update').value) {
      case true:
        return 'Sent';
      case false:
        return 'Unsent';
    }
    return '';
  }

  getRefillStatusString(): string {
    switch (this.clientForm.get('remind_zero_refill').value) {
      case true:
        return 'Send';
      case false:
        return 'Don`t Send';
    }
    return '';
  }

  editClient(id) {
    this.navigationService.navigateTo('lists/clients/edit/' + id);
  }

  putDash(e) {
    let x = e;
    if (x.length === 6 && x.charAt(5) !== '-') {
      x = x.slice(0, 5) + '-' + x.charAt(x.length - 1);
    }
    return x;
  }

  changeTab(index) {
    this.tabs = this.tabs.map((item) => {
      item.active = item.index === index;
      return item;
    });
    this.insertParam(index);
  }

  insertParam(value) {
    if (this.fromCanvas) return;
    const queryParams = { tab_index: +value };
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
